import * as yup from "yup";
import { CantidadSchema } from "./CantidadSchema";
import { PuntoGeograficoSchema } from "./PuntoGeograficoSchema";
import { AnalisisLaboratorioSchema } from "./AnalisisLaboratorioSchema";
import { DestinoVuelco, PeriodicidadVuelco } from "../utils/enums";

export const LineaVertidoSchema = yup.object().shape({
  orden: yup.number().required("errors.lineaVertido.orden.required"),
  nombre: yup
    .string()
    .max(100, "errors.lineaVertido.nombre.max")
    .required("errors.lineaVertido.nombre.required")
    .default(""),
  destino: yup
    .object()
    .required("errors.lineaVertido.destino.required")
    .default(null),
  periodicidad: yup
    .object()
    .required("errors.lineaVertido.periodicidad.required")
    .default(null),
  horasVuelco: CantidadSchema.typeError("errors.lineaVertido.horasVuelco.type")
    .required("errors.lineaVertido.horasVuelco.required")
    .when(["periodicidad"], ([periodicidad], schema) => {
      if (periodicidad?.codigo === PeriodicidadVuelco.DISCONTINUO) {
        return schema.test(
          "magnitud.max",
          "errors.cantidad.magnitud.min",
          (value, context) => {
            return value?.magnitud <= 24 * 31 * 12;
          },
        );
      } else if (periodicidad?.codigo === PeriodicidadVuelco.MENSUAL) {
        return schema.test(
          "magnitud.max",
          "errors.lineaVertido.horasVuelco.max",
          (value, context) => {
            return value?.magnitud <= 24 * 31;
          },
        );
      } else if (periodicidad?.codigo === PeriodicidadVuelco.SEMANAL) {
        return schema.test(
          "magnitud.max",
          "errors.lineaVertido.horasVuelco.max",
          (value, context) => {
            return value?.magnitud <= 24 * 7;
          },
        );
      } else if (periodicidad?.codigo === PeriodicidadVuelco.DIARIO) {
        return schema.test(
          "magnitud.max",
          "errors.lineaVertido.horasVuelco.max",
          (value, context) => {
            return value?.magnitud <= 24;
          },
        );
      } else {
        return schema.test(
          "magnitud.max",
          "errors.lineaVertido.horasVuelco.max",
          (value, context) => {
            return value?.magnitud <= 24;
          },
        );
      }
    })
    .test("magnitud.min", "errors.cantidad.magnitud.min", (value, context) => {
      return value?.magnitud > 0;
    })
    .default({ magnitud: 0, unidad: { codigo: "H" } }),
  caudalMedio: CantidadSchema.required(
    "errors.lineaVertido.caudalMedio.required",
  )
    .test("magnitud.min", "errors.cantidad.magnitud.min", (value, context) => {
      return value?.magnitud > 0;
    })
    .default({ magnitud: 0, unidad: { codigo: "M3DIA" } }),
  caudalPico: CantidadSchema.required("errors.lineaVertido.caudalPico.required")
    .test("magnitud.min", "errors.cantidad.magnitud.min", (value, context) => {
      return value?.magnitud > 0;
    })
    .default({ magnitud: 0, unidad: { codigo: "M3H" } }),
  caudalTeorico: CantidadSchema.required(
    "errors.lineaVertido.caudalTeorico.required",
  )
    .required()
    .test("magnitud.min", "errors.cantidad.magnitud.min", (value, context) => {
      return value?.magnitud > 0;
    })
    .default({ magnitud: 0, unidad: { codigo: "M3DIA" } }),
  ubicacionCTM: PuntoGeograficoSchema.required(
    "errors.lineaVertido.ubicacionCTM.required",
  ).default(PuntoGeograficoSchema.cast(undefined, { assert: false })),
  prestador: yup.object().when(["destino"], ([destino], schema) => {
    if (destino?.codigo === DestinoVuelco.CLOACAL) {
      return schema
        .required("errors.lineaVertido.prestador.required")
        .default(null);
    } else {
      return schema.optional().nullable();
    }
  }),
  subcuenca: yup.object().when(["destino"], ([destino], schema) => {
    if (destino?.codigo === DestinoVuelco.SUPERFICIAL) {
      return schema
        .required("errors.lineaVertido.subcuenca.required")
        .default(null);
    } else {
      return schema.optional().nullable().default(null);
    }
  }),
  conPermisoVuelco: yup
    .boolean()
    .required("errors.lineaVertido.conPermisoVuelco.required")
    .default(false),
  permisoVuelco: yup
    .string()
    .when(
      ["conPermisoVuelco", "destino"],
      ([conPermisoVuelco, destino], schema) => {
        if (conPermisoVuelco) {
          if (destino?.codigo === DestinoVuelco.CLOACAL) {
            return schema
              .required("errors.lineaVertido.permisoVuelco.required")
              .default("");
          } else {
            return schema.required(
              "errors.lineaVertido.permisoVuelco.required",
            );
          }
        } else {
          return schema.optional().nullable();
        }
      },
    ),
  ubicacion: PuntoGeograficoSchema.when(["destino"], ([destino], schema) => {
    if (destino?.codigo === DestinoVuelco.SUPERFICIAL) {
      return schema
        .required("errors.lineaVertido.ubicacion.required")
        .default(PuntoGeograficoSchema.cast(undefined, { assert: false }));
    } else {
      return schema.optional().nullable().default(null);
    }
  }),
  caracterizacion: yup
    .array()
    .of(AnalisisLaboratorioSchema)
    .min(1, "errors.lineaVertido.caracterizacion.min")
    .required("errors.lineaVertido.caracterizacion.required")
    .default([]),
});
